import type { EditableGridCell, TextCell } from '@glideapps/glide-data-grid'
import { GridCellKind } from '@glideapps/glide-data-grid'
import { CLEAR_SENTINEL } from './MetadataHandling/CSVClearSentinel.js'
import type { EnumOption } from './MetadataHandling/EnumColumns.js'

type Props = {
  value: string
  options: EnumOption[]
  onChange: (newValue: EditableGridCell) => void
  onFinishedEditing: (newValue?: EditableGridCell) => void
}

const asCell = (data: string): TextCell => ({
  kind: GridCellKind.Text,
  data,
  displayData: data,
  allowOverlay: true
})

/**
 * Overlay editor for a column whose values are a closed set.
 *
 * A `<select>` rather than a custom grid cell: glide renders cells to canvas, so a real dropdown cell
 * needs its own renderer (or the extras package). `provideEditor` replaces only the editor the overlay
 * opens, which is where the choice actually has to be made, and costs no dependency.
 *
 * Two entries are not enum members. The clear token is offered explicitly, because typing `NULL` into
 * a column that only accepts known keys is not a guessable affordance. A value already in the cell
 * that is *not* a known key is offered too — the grid must not silently drop data it cannot classify,
 * and seeing it listed as unknown is how the user finds out it will not import.
 */
export const EnumCellEditor = ({ value, options, onChange, onFinishedEditing }: Props) => {
  const known = options.some((o) => o.key === value)
  const isClear = value.trim().toLowerCase() === CLEAR_SENTINEL.toLowerCase()

  return (
    <select
      className="select select-sm w-full h-full border-0 focus:outline-none"
      autoFocus
      value={value}
      onChange={(e) => {
        onChange(asCell(e.target.value))
        onFinishedEditing(asCell(e.target.value))
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onFinishedEditing(undefined)
      }}
    >
      <option value="">(empty — leave unchanged on import)</option>
      <option value={CLEAR_SENTINEL}>{CLEAR_SENTINEL} — clear the stored value</option>
      {!known && !isClear && value !== '' && <option value={value}>{value} (not a known value)</option>}
      {options.map((o) => (
        <option key={o.key} value={o.key}>
          {o.label}
        </option>
      ))}
    </select>
  )
}
